import { prepareDdlEditContext } from './prepareDdlEditContext';
// previewDdlEdit — compute the payload for the preview-before-commit dialog.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.2 — Commit Pipeline + Buffer Sync.
//
// Goes through the DSE2 pipeline via `resolveDdlEdit` but never calls
// `model.applyEdits` — Monaco stays untouched until the user confirms.
// Returns the payload the dialog renders: the planned byte edits, the
// post-edit emitted source, and a small inline diff HTML fragment.
//
// Diff format: a single-pass walk that intersperses the source with `<del>`
// and `<ins>` tags around each `PlannedByteEdit`. HTML-escapes everything
// except the diff tags. Multi-edit patches render as one HTML fragment with
// edits placed in source order. Empty `plannedEdits` returns the original
// source escaped (no diff markers).

import type { DdlEdit, PlannedByteEdit } from '../../../sql-resolver/types';
import { resolveDdlEdit, type ResolveDdlEditCtx } from './resolveDdlEdit';

export type PreviewDdlEditPayload = {
  readonly emittedSource: string;
  readonly plannedEdits: ReadonlyArray<PlannedByteEdit>;
  readonly diffHtml: string;
};

export async function previewDdlEdit(
  edit: DdlEdit,
  ctx: ResolveDdlEditCtx,
): Promise<PreviewDdlEditPayload> {
  const resolved = resolveDdlEdit(edit, await prepareDdlEditContext(edit, ctx));
  return {
    emittedSource: resolved.emittedSource,
    plannedEdits: resolved.modified.plannedEdits,
    diffHtml: renderDiffHtml(
      resolved.originalSource,
      resolved.modified.plannedEdits,
    ),
  };
}

/** Renders a `<del>old</del><ins>new</ins>`-marked HTML fragment. */
export function renderDiffHtml(
  source: string,
  edits: ReadonlyArray<PlannedByteEdit>,
): string {
  if (edits.length === 0) return escapeHtml(source);
  const sorted = [...edits].sort(
    (a, b) => a.range.startOffset - b.range.startOffset,
  );
  const parts: string[] = [];
  let cursor = 0;
  for (const e of sorted) {
    if (e.range.startOffset > cursor) {
      parts.push(escapeHtml(source.slice(cursor, e.range.startOffset)));
    }
    if (e.range.endOffset > e.range.startOffset) {
      parts.push(
        `<del>${escapeHtml(
          source.slice(e.range.startOffset, e.range.endOffset),
        )}</del>`,
      );
    }
    if (e.replacement.length > 0) {
      parts.push(`<ins>${escapeHtml(e.replacement)}</ins>`);
    }
    cursor = e.range.endOffset;
  }
  if (cursor < source.length) {
    parts.push(escapeHtml(source.slice(cursor)));
  }
  return parts.join('');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
